import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
    DefaultButton,
    IconButton,
    IIconProps,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"

import {contentStyles, iconButtonStyles} from "./Style"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"

import {FormProvider, useForm} from "react-hook-form"
import {useBranding} from '../../hooks/useBranding'
import ApprovalService, {
    IGetApprovalListMasterResponse,
    IPostApprovalListMasterRequest,
    IPutApprovalListMasterRequest
} from "../../services/Approval"
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl"
import {decode} from 'html-entities'

type Properties = {
    showModal: boolean
    onDismissModal: (update: boolean) => void;
    item: IGetApprovalListMasterResponse | undefined
    documentOnlyAudienceId: string
}

export function ApprovalListEdit({
                                     documentOnlyAudienceId,
                                     item,
                                     onDismissModal,
                                     showModal,
                                 }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const cancelIcon: IIconProps = {iconName: 'Cancel'}
    const form = useForm({
        defaultValues: {
            Name: '',
            Description: ''
        }
    })

    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)


    useEffect(() => {
        if (showModal) {
            setShowMessage(false)

            form.reset()
            form.clearErrors()
            form.setValue("Name", item?.Name ?? '')
            form.setValue("Description", item?.Description ?? '')
        }
    }, [showModal])

    const update = async () => {
        await form.handleSubmit(async () => {

                form.clearErrors()
                setIsSaving(true)

                const requestKIM: IPutApprovalListMasterRequest = {
                    Name: form.getValues("Name"),
                    Description: form.getValues("Description"),
                    AudienceId: documentOnlyAudienceId,
                    Id: item?.Id.toString() ?? ''
                }

                await ApprovalService.putApprovalListMaster(requestKIM)
                    .then(data => {
                        if (data.Success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )()
    }

    /**
     * Displays a status message with the specified content and status type.
     *
     * @function ShowStatusMessage
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - An enumeration representing the type or status of the message (e.g., success, error).
     * @returns {void}
     */
    const ShowStatusMessage = (message: string, status: StatusEnum): void => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * Asynchronous function to handle the creation of an approval step request.
     *
     * This function interacts with a form, retrieves form values, validates them, and submits validation errors if present.
     * Upon successful form submission, it sends a POST request to the ApprovalService to create an approval step.
     * Proper success or error messages are displayed to the user depending on the result of the request.
     *
     * The request data includes approval ID, name, and template ID.
     * The function also manages the saving state indicator and handles any errors that may occur during the request.
     *
     * Error messages and success statuses are displayed using localized strings from a translation function (`t`)
     * with appropriate namespaces (`ns`).
     *
     * @async
     * @function create
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const create = async (): Promise<void> => {
        await form.handleSubmit(async () => {

                form.clearErrors()
                setIsSaving(true)

                const requestKIM: IPostApprovalListMasterRequest = {
                    Name: form.getValues("Name"),
                    Description: form.getValues("Description"),
                    AudienceId: documentOnlyAudienceId
                }

                await ApprovalService.postApprovalListMaster(requestKIM)
                    .then(data => {
                        if (data.Success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )()
    }

    return (
        <>


            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerListEdit}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"} id={'approvalStepEditTitle'}
                                  title={item === undefined ? t('title.newList', {ns: 'approval'}) : t('title.editList', {ns: 'approval'})}>
                                {item === undefined ? t('title.newList', {ns: 'approval'}) : t('title.editList', {ns: 'approval'})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={cancelIcon}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={() => {
                                    onDismissModal(false)
                                }}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">


                                    {showMessage &&
                                        <>
                                            <StackItem styles={{root: {marginBottom: 16}}}>

                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </StackItem>

                                        </>
                                    }

                                    <StackItem styles={{root: {paddingTop: 8}}}>
                                        <FormProvider {...form}>
                                            <form onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
                                                <Stack>
                                                    <StackItem grow={1}>

                                                        <RHFTextBoxControl
                                                            id={"Name"}
                                                            label={t('label.ctrlName', {ns: 'common'})}

                                                            rules={{
                                                                required: t('text.fieldRequired', {ns: 'common'}),
                                                                validate: {
                                                                    required:
                                                                        (value) => {
                                                                            return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                                        }
                                                                },
                                                            }}

                                                            placeholder={t('label.ctrlName', {ns: 'common'})}
                                                            title={t('label.ctrlName', {ns: 'common'})}
                                                            ariaLabel={t('label.ctrlName', {ns: 'common'})}
                                                            maxLength={50}
                                                            control={form.control}
                                                            setValue={form.setValue}
                                                            getValues={form.getValues}
                                                            trigger={form.trigger}
                                                            disabled={isSaving || item !== undefined}


                                                            required
                                                        >
                                                        </RHFTextBoxControl>


                                                        <RHFTextBoxControl
                                                            id={"Description"}
                                                            label={t('label.ctrlDescription', {ns: 'common'})}

                                                            rules={{
                                                                required: t('text.fieldRequired', {ns: 'common'}),
                                                                validate: {
                                                                    required:
                                                                        (value) => {
                                                                            return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                                        }
                                                                },
                                                            }}

                                                            placeholder={t('label.ctrlDescription', {ns: 'common'})}
                                                            title={t('label.ctrlDescription', {ns: 'common'})}
                                                            ariaLabel={t('label.ctrlDescription', {ns: 'common'})}
                                                            maxLength={2000}
                                                            control={form.control}
                                                            setValue={form.setValue}
                                                            getValues={form.getValues}
                                                            trigger={form.trigger}
                                                            disabled={isSaving}
                                                            required
                                                        >
                                                        </RHFTextBoxControl>

                                                    </StackItem>

                                                </Stack>

                                            </form>
                                        </FormProvider>
                                    </StackItem>


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>


                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {isSaving &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinnerSaving', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <StackItem>


                                        {item !== undefined &&
                                            <>
                                                <PrimaryButton disabled={isSaving}
                                                               style={{marginLeft: 8}}
                                                               onClick={update}
                                                               text={t('Common.Button.Update', {ns: 'common'})}
                                                               title={t('Common.Button.Update', {ns: 'common'})}/>
                                            </>
                                        }
                                        {item === undefined &&
                                            <PrimaryButton disabled={isSaving}
                                                           style={{marginLeft: 8}}
                                                           onClick={create}
                                                           text={t('Common.Button.Create', {ns: 'common'})}
                                                           title={t('Common.Button.Create', {ns: 'common'})}/>
                                        }

                                        <DefaultButton
                                            onClick={() => {
                                                onDismissModal(false)
                                            }}
                                            style={{marginLeft: 8}}
                                            text={t('Common.Button.Close', {ns: 'common'})}
                                            title={t('Common.Button.Close', {ns: 'common'})}/>

                                    </StackItem>
                                </Stack>

                                {/*-- END FOOTER --*/}

                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Modal>

        </>
    )
}
